import { Injectable } from '@angular/core';
import { HttpService } from './http.service';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, tap, catchError, of } from 'rxjs';
import { HttpErrorHandlerService } from './http-error-handler.service';
import { HttpHeaders } from '@angular/common/http';
import { UserStateService } from './user-state.service';
import { NotificationService } from './api/notification.service';
import { ChatMessagesService } from './chat-messages.service';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { Capacitor } from '@capacitor/core';
// Firebase Web SDK for browser push
import { getApp, getApps, initializeApp } from 'firebase/app';
import { getMessaging, getToken as getWebFcmToken, onMessage } from 'firebase/messaging';
import { environment } from 'src/environments/environment';
import { AppStorageService } from './app-storage.service';
import { StorageKeys } from './storage-keys';
import { ChatService } from './api/chat.service';
import { SocialLogin } from '@capgo/capacitor-social-login';
import { ToastService } from '../services/toast.service';
import { isNativePushPlatform } from './utils/runtime-config.util';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private isLoggedInSubject = new BehaviorSubject<boolean>(this.checkToken()); // Estado del usuario

  constructor(
    private httpService: HttpService,
    private errorHandler: HttpErrorHandlerService,
    private router: Router,
    private userStateService: UserStateService,
    private notificationService: NotificationService,
    private chatMessagesService: ChatMessagesService,
    private chatService: ChatService,
    private toastService: ToastService,
    private storage: AppStorageService
  ) {
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      this.isLoggedInSubject.next(this.checkToken());
      // Si el usuario ya está autenticado al cargar la aplicación, configuramos las notificaciones
      if (this.checkToken()) {
        if (isNativePushPlatform(Capacitor.getPlatform())) {
          FirebaseMessaging.requestPermissions();
        }
        this.setupNotificationsAfterLogin();
      }
    }
  }

  /**
   * Obtiene el token FCM actual desde el plugin y lo retorna; además muestra un toast para debug.
   */
  /**
   * Obtiene el token FCM actual desde el plugin y lo retorna.
   * Si ya existe un token en localStorage, no lo sobrescribe a menos que force=true.
   */
  async showCurrentFcmToken(force = false, persist = false): Promise<string | null> {
    try {
      const existing = typeof localStorage !== 'undefined' ? this.storage.get(StorageKeys.FCM_TOKEN) : null;
      if (!force && existing && existing.length > 8) {
        return existing;
      }
      let token: string | null = null;
      if (Capacitor.getPlatform() === 'web') {
        // Web: use Firebase Web SDK with VAPID key
        const app = getApps().length > 0
          ? getApp()
          : initializeApp(environment.firebase as any);
        const messaging = getMessaging(app);
        token = await getWebFcmToken(messaging, { vapidKey: (environment as any).vapidKey || undefined });
      } else {
        const result: any = await FirebaseMessaging.getToken();
        token = result?.token || null;
      }
  // debug toast removed

      if (token && persist) {
        this.storage.set(StorageKeys.FCM_TOKEN, token);
      }
      return token;
    } catch (e) {
  // debug toast removed
      return null;
    }
  }

  public getToken(): string | null {
    try {
      return typeof localStorage !== 'undefined'
        ? this.storage.get(StorageKeys.TOKEN)
        : null;
    } catch (error) {
      return null;
    }
  }

  private checkToken(): boolean {
    return !!this.getToken();
  }

  isAuthenticated(): boolean {
    return this.isLoggedInSubject.value;
  }

  isAuthenticated$(): Observable<boolean> {
    return this.isLoggedInSubject.asObservable();
  }

  /**
   * Configura las notificaciones después del login
   */
  private setupNotificationsAfterLogin(): void {
    // Debug toasts (gated for iOS or debugPush flag)
  // debug toasts removed

  // debug toasts removed
    if (isNativePushPlatform(Capacitor.getPlatform())) {
      FirebaseMessaging.requestPermissions();
      this.setupPushUnTopic(this.storage.get(StorageKeys.FCM_TOKEN));
      this.setupPushTopic();
    }
  }

  /**
   * Resetea los contadores de notificaciones y mensajes
   */
  private resetCounters(): void {
    // Resetear contador de mensajes no leídos
    this.chatMessagesService.updateUnreadMessages(0);

    // Limpiar localStorage de contadores
    this.storage.remove(StorageKeys.NOTIFICATION_COUNT);
    this.storage.remove(StorageKeys.UNREAD_MESSAGES);
  }

  /**
   * Inicia sesión con email y contraseña.
   * @param email Email del usuario.
   * @param password Contraseña del usuario.
   * @returns Observable con la respuesta del backend.
   */
  login(email: string, password: string): Observable<any> {
    const lang = this.storage.get(StorageKeys.LANG) || 'es';
    return this.httpService
      .post('login', { email, password, language: lang })
      .pipe(
        tap((res: any) => {
          void this.setLogging(res);
          this.refreshNotificationTokenAfterNativeLogin();
        }),
        catchError((error) => this.errorHandler.handleError(error))
      );
  }

  /**
   * Registra un nuevo usuario en el sistema.
   * @param userData Datos del usuario a registrar (nombre, apellido, teléfono, email, contraseña).
   * @returns Observable con la respuesta del backend.
   */
  register(userData: any): Observable<any> {
    const lang = this.storage.get(StorageKeys.LANG) || 'es';
    return this.httpService
      .post('register', { ...userData, language: lang })
      .pipe(
        tap((res: any) => {
          void this.setLogging(res);
        }),
        catchError((error) => this.errorHandler.handleError(error))
      );
  }

  /**
   * Envía un correo para recuperar la contraseña.
   * @param email Email del usuario.
   * @returns Observable con la respuesta del backend.
   */
  forgetPassword(email: string): Observable<any> {
    return this.httpService.post('forgot-password', { email }).pipe(
      tap((res: any) => {
        return res;
        // this.toastService.showSuccess('Correo enviado', res.message);
      }),
      catchError((error) => this.errorHandler.handleError(error))
    );
  }

  /**
   * Solicita la eliminación de la cuenta del usuario actual
   * @returns Observable con la respuesta del backend
   */
  deleteAccount(): Observable<any> {
    return this.httpService.delete('delete-account').pipe(
      tap((res: any) => {
        // Limpiamos toda la información del usuario al eliminar la cuenta
        this.storage.remove(StorageKeys.TOKEN);
        this.storage.remove(StorageKeys.USER);
        this.storage.remove(StorageKeys.FCM_TOKEN);
        this.storage.remove(StorageKeys.NOTIFICATION_COUNT);
        this.storage.remove(StorageKeys.UNREAD_MESSAGES);

        // Limpia el estado del usuario en UserStateService
        this.userStateService.updateUser(null);

        // Resetea los contadores
        this.resetCounters();

        this.isLoggedInSubject.next(false); // Actualiza el estado inmediatamente
      }),
      catchError((error) => this.errorHandler.handleError(error))
    );
  }

  /**
   * Regenera el token API (token_api) en backend y reconfigura los tópicos de notificaciones.
   * Debe llamarse al iniciar la app si el usuario está autenticado.
   */
  regenerateTokenApi(): Observable<any> {
    // Si no está autenticado, no hacer nada
    if (!this.isAuthenticated()) {
      return of({ success: false, skipped: true, reason: 'not_authenticated' });
    }
    return this.httpService.post('regenerate-token-api', {}).pipe(
      tap((res: any) => {
        if (res?.success && res?.user?.token_api) {
          try {
            const current = this.storage.getJSON<any>(StorageKeys.USER) || {};
            this.storage.setJSON(StorageKeys.USER, { ...current, ...res.user });
          } catch (e) {}

          this.syncNotificationToken(res.user.token_api);
        }
      }),
      catchError((error) => this.errorHandler.handleError(error))
    );
  }

  syncNotificationToken(tokenApi: string | null | undefined): void {
    if (!tokenApi) {
      return;
    }

    const oldToken = this.storage.get(StorageKeys.FCM_TOKEN);
    if (oldToken && oldToken !== tokenApi) {
      this.setupPushUnTopic(oldToken);
    }

    this.storage.set(StorageKeys.FCM_TOKEN, tokenApi);
    this.storage.setAsync(StorageKeys.TOKEN_PUSH, tokenApi);
    this.setupPushTopic(tokenApi);

    try {
      const currentUser = this.storage.getJSON<any>(StorageKeys.USER) || {};
      this.storage.setJSON(StorageKeys.USER, { ...currentUser, token_api: tokenApi });
    } catch (e) {}
  }

  /**
   * Cierra sesión eliminando el token del almacenamiento local.
   */
  logout(): Observable<any> {
    return this.httpService.post('logout', {}, new HttpHeaders({})).pipe(
      tap(async () => {
        this.storage.getAsync(StorageKeys.TOKEN_PUSH).then((data) => {
          if (data) {
            this.setupPushUnTopic(data);
          }
        });

        // Intentar cerrar sesión de proveedores sociales para limpiar credenciales persistidas
        try {
          // Google / Facebook / Apple vía plugin (ignorar errores si no había sesión)
          await Promise.all([
            (async ()=>{ try { await (SocialLogin as any).logout?.({ provider: 'google' }); } catch {} })(),
            (async ()=>{ try { await (SocialLogin as any).logout?.({ provider: 'facebook' }); } catch {} })(),
            (async ()=>{ try { await (SocialLogin as any).logout?.({ provider: 'apple' }); } catch {} })(),
          ]);
        } catch(e) {  }

        // Firebase Auth signOut para web (asegura que el selector de cuentas vuelva a mostrarse)
        try {
          if (typeof window !== 'undefined') {
            const { getAuth, signOut } = await import('@angular/fire/auth');
            try { await signOut(getAuth()); } catch {}
          }
        } catch (e) {  }

        // Limpia el estado del usuario en UserStateService
        this.userStateService.updateUser(null);
        this.storage.set(StorageKeys.TOKEN, '');
        this.storage.set(StorageKeys.USER, '');
        this.storage.set(StorageKeys.GUEST, 'true');
        this.storage.remove(StorageKeys.FCM_TOKEN);

        // Limpieza adicional de storage social (por si algún provider guarda algo)
        try {
          this.storage.remove(StorageKeys.FIREBASE_AUTH_USER);
          this.storage.remove(StorageKeys.FIREBASE_REDIRECT_EVENT);
        } catch {}

        // Resetea los contadores
        this.resetCounters();

        this.isLoggedInSubject.next(false); // Actualiza el estado inmediatamente
      }),
      catchError((error) => this.errorHandler.handleError(error))
    );
  }

  socialLogin(
    provider: 'google' | 'facebook' | 'apple',
    userData: any
  ): Observable<any> {
    const lang = this.storage.get(StorageKeys.LANG) || 'es';

    return this.httpService
      .post('rrss', { provider: provider, ...userData, language: lang })
      .pipe(
        tap(async (res: any) => {
          this.storage.set(StorageKeys.TOKEN, res.token);
          this.storage.set(StorageKeys.GUEST, 'false');
          this.storage.setJSON(StorageKeys.USER, res.user);

          const candidate = res.user.token_api ?? res.token_api;
          const oldToken = this.storage.get(StorageKeys.FCM_TOKEN);
          if (oldToken) {
            this.setupPushUnTopic(oldToken);
            this.storage.remove(StorageKeys.FCM_TOKEN);
          }
          this.syncNotificationToken(candidate);

          // Actualiza el estado del usuario en UserStateService
          this.userStateService.updateUser(res.user);

          this.isLoggedInSubject.next(true); // Actualiza el estado inmediatamente

          // Maneja el token FCM
          // debug toast removed
          if (isNativePushPlatform(Capacitor.getPlatform())) {
            await FirebaseMessaging.requestPermissions();
          }
          // debug toast removed

          // Configura las notificaciones después del login
          this.setupNotificationsAfterLogin();
          this.refreshNotificationTokenAfterNativeLogin();
          // debug toast removed
        })
      );
  }

  async setupPushTopic(topic: any = this.storage.get(StorageKeys.FCM_TOKEN)) {
    if (topic && topic !== 'undefined') {
      if (Capacitor.isNativePlatform()) {
        try {
          // Resolve user id if available for context
          let userId: string | null = null;
          try {
            const userRaw = this.storage.get(StorageKeys.USER);
            if (userRaw) {
              const userObj = JSON.parse(userRaw);
              userId = userObj?.id ?? userObj?.user_id ?? null;
            }
          } catch (e) {
          }

          // Log token details to help debugging
          const tokenPreview = String(topic).slice(0, 8) + '...' + String(topic).slice(-6);
          await FirebaseMessaging.subscribeToTopic({ topic });
          // debug toast removed
        } catch (e) {
          // debug toast removed
        }
      } else {
      }
    }else {
    }
  }

  async setupPushUnTopic(topic: any) {
    if (topic && topic !== 'undefined') {
      if (Capacitor.isNativePlatform()) {

        try {
          await FirebaseMessaging.unsubscribeFromTopic({ topic });
        } catch (e) {
        }
      } else {
      }
    }
  }

  private refreshNotificationTokenAfterNativeLogin(): void {
    if (!isNativePushPlatform(Capacitor.getPlatform())) {
      return;
    }

    this.regenerateTokenApi().subscribe({
      error: () => {},
    });
  }

  private async setLogging(res: any) {
    this.storage.set(StorageKeys.GUEST, 'false');
    this.storage.set(StorageKeys.TOKEN, res.token);
    this.storage.setJSON(StorageKeys.USER, res.user);

    const oldToken = this.storage.get(StorageKeys.FCM_TOKEN);
    if(oldToken){
      this.setupPushUnTopic(oldToken);
      this.storage.remove(StorageKeys.FCM_TOKEN);
    }

    const candidateToken = res.user.token_api ?? res.token_api;
    this.syncNotificationToken(candidateToken);

    // Actualiza el estado del usuario en UserStateService
    this.userStateService.updateUser(res.user);

    this.isLoggedInSubject.next(true); // Actualiza el estado inmediatamente

    // Maneja el token FCM
  // debug toast removed
    if (isNativePushPlatform(Capacitor.getPlatform())) {
      await FirebaseMessaging.requestPermissions();
    }
  // debug toast removed

    // Configura las notificaciones después del login
    this.setupNotificationsAfterLogin();
  // debug toast removed
    // Sincronizar idioma que venga del servidor (si existe) sin re-persistirlo
    try {
      const serverLang = res?.user?.language || res?.language || null;
      if (serverLang) {
        // Obtener TranslateConfigService de forma perezosa para evitar dependencia circular
        try {
          const injector = (window as any).ng && (window as any).ng.getInjector ? (window as any).ng.getInjector(document.body) : null;
          // Fallback: dynamic import
          let translateConfig: any = null;
          if (injector && injector.get) {
            translateConfig = injector.get((await import('../services/translate-config.service')).TranslateConfigService);
          } else {
            const mod = await import('../services/translate-config.service');
            translateConfig = (mod as any).TranslateConfigService ? (mod as any).TranslateConfigService : null;
          }
          if (translateConfig && typeof translateConfig.setLanguage === 'function') {
            // Apply server value but do not persist back (persistToServer = false)
            translateConfig.setLanguage(serverLang, false);
          }
        } catch (e) {
          // If we cannot lazy-resolve the service, ignore — it's non-critical
        }
      }
    } catch (e) {}
  }

}
